import React from "react";
import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";

import { fieldLabels } from "../../item/fieldLabels";
import {
  ActivityBody,
  AssignRecipientBody,
  AttachmentActivityBody,
  AttachmentBodyProps,
} from "./types";
import { InformationRequest } from "../../../domain/InformationRequest";
import { ActivityType } from "../../../domain/ActivityType";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { useSelector } from "react-redux";
import { authSelector } from "../../../redux/selectors";
import { informationRequestService } from "../../../services";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { Chip } from "@material-ui/core";
import ReactMarkdown from "react-markdown";

export const buildDefinitions = (
  setFocussedActivity: (activity: Activity) => void,
  informationRequest: InformationRequest,
  requestTypeLookups: ReturnType<typeof useLookup>[0]
) => {
  const requestUpdate: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: ActivityType.InformationRequestUpdated,
    Body: ({ body }) => (
      <>
        <b>Updated: </b>
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  const requestSent: ActivityFeedItemDefinition<{ requestType: string }> = {
    activityType: ActivityType.InformationRequestSent,
    Body: ({ body }) => (
      <>
        {requestTypeLookups.find((x) => x.id === +body.requestType)?.label ||
          ""}
      </>
    ),
    options: [],
  };

  const requestSentChild: ActivityFeedItemDefinition<{
    requestType: string;
  }> = {
    activityType: ActivityType.InformationRequestSentChild,
    Body: ({ body }) => (
      <>
        {requestTypeLookups.find((x) => x.id === +body.requestType)?.label ||
          ""}
      </>
    ),
    options: [],
  };

  const assignedRecipients: ActivityFeedItemDefinition<AssignRecipientBody> = {
    activityType: ActivityType.InformationRequestRecipientAssigned,
    Body: ({ body }) => (
      <>
        <b>Assigned: </b>
        {body.assignments.map((item, index) => (
          <div key={`${item}-${index}`}>
            <br />
            {item.assignedToDisplayName} to {item.organisationName}
          </div>
        ))}
      </>
    ),
    options: [],
  };

  const AttachmentBody: React.FC<AttachmentBodyProps> = ({ bodyProps }) => {
    const { body } = bodyProps;
    const { token } = useSelector(authSelector);

    const buildGetBlob = (blobId: string) => () =>
      informationRequestService.downloadActivityDocument(
        informationRequest.id,
        bodyProps.activity.id,
        blobId,
        { token: token }
      );
    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((document) => (
            <li
              key={document.blobId}
              style={{ listStyleType: "none" }}
              data-test="attachment-activity-button"
            >
              <DownloadLink
                fileName={document.fileName}
                getBlob={buildGetBlob(document.blobId)}
              />
            </li>
          ))}
        </ul>
      </>
    );
  };

  const recipientMessageOfqualReceived: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.InformationRequestRecipientMessage,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown linkTarget="_blank">{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
  };

  const recipientMessageOfqualSent: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.InformationRequestMessageSent,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.recipientOrgs ? (
            <>
              <b>Recipients: </b>
              {body.recipientOrgs.map((item, index) => (
                <Chip
                  size="small"
                  style={{ margin: 2, display: "flex-grid" }}
                  label={item}
                  className="ChangeChip"
                  key={index}
                />
              ))}
            </>
          ) : null}
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown linkTarget="_blank">{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
  };

  const respondToMessageAction: ActivityFeedItemDefinition<{
    message: string;
    org?: string;
  }> = {
    activityType: ActivityType.InformationRequestRespondToMessageAction,
    Body: ({ body }) => {
      return (
        <div>
          {body.org ? (
            <div>
              <b>{body.org}</b> responded to message action. <br />
            </div>
          ) : null}
          <b>Message: </b>
          <ReactMarkdown linkTarget="_blank">{body.message}</ReactMarkdown>
        </div>
      );
    },
  };

  const respondToDocumentAction: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.InformationRequestUploadDocumentsAction,
    Body: ({ body, activity }) => {
      return (
        <div>
          {body.org ? (
            <div>
              <b>{body.org}</b> responded to document action. <br />
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </div>
      );
    },
  };

  const readDocumentActivity: ActivityFeedItemDefinition<{ org: string }> = {
    activityType: ActivityType.InformationRequestReadDocumentsAction,
    Body: ({ body }) => {
      return (
        <div>
          {body.org ? (
            <div>
              <b>{body.org}</b> read required documents <br />
            </div>
          ) : null}
        </div>
      );
    },
  };

  const messageAOReceived: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.InformationRequestRecipientMessageAO,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown linkTarget="_blank">{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
  };

  const messageAOSent: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.InformationRequestMessageSentAO,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown linkTarget="_blank">{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
    align: "left",
  };

  const aoAssigned: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.InformationRequestAssignedAO,
  };

  const aoUpdateStaff: ActivityFeedItemDefinition<{ users: string[] }> = {
    activityType: ActivityType.InformationRequestUpdateRecipientsAO,
    Body: ({ body }) => (
      <>
        <b>Staff: </b>
        {body.users &&
          body.users.map((item, index) => (
            <div key={`${item}-${index}`}>
              <br />
              {item}
            </div>
          ))}
      </>
    ),
  };

  const aoRemoveStaff: ActivityFeedItemDefinition<{ users: string[] }> = {
    activityType: ActivityType.InformationRequestRemoveRecipientsAO,
    Body: ({ body }) => (
      <>
        <b>Removed Staff: </b>
        {body.users &&
          body.users.map((item, index) => (
            <div key={`${item}-${index}`}>
              <br />
              {item}
            </div>
          ))}
      </>
    ),
  };

  const closedRecipientAO: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.InformationRequestRecipientClosedAO,
  };

  const closedRecipientOfqual: ActivityFeedItemDefinition<{
    recipients: string[];
  }> = {
    activityType: ActivityType.InformationRequestRecipientClosedOfqual,
    Body: ({ body }) => (
      <>
        <b>Closed recipients: </b>
        {body.recipients &&
          body.recipients.map((item, index) => (
            <div key={`${item}-${index}`}>
              <br />
              {item}
            </div>
          ))}
      </>
    ),
  };

  const assignChildOfqual: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.InformationRequestRecipientAssignedChild,
  };

  return [
    requestUpdate,
    requestSent,
    assignedRecipients,
    recipientMessageOfqualReceived,
    recipientMessageOfqualSent,
    respondToMessageAction,
    respondToDocumentAction,
    readDocumentActivity,
    messageAOReceived,
    messageAOSent,
    aoAssigned,
    aoUpdateStaff,
    aoRemoveStaff,
    closedRecipientAO,
    closedRecipientOfqual,
    assignChildOfqual,
    requestSentChild,
  ];
};
